/**
 * Small pieces the People screens share (screenshots/18–24, 29–30): type chip, policy-area chip, stance chip,
 * portrait / video-frame images (circles are official portraits; rounded rectangles with a film icon are
 * video frames at the time of comment, never called photos), watch links, and the peek trigger.
 */
import { memo, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Briefcase, Building2, Film, House, Landmark, Mic, Play, ThumbsDown, ThumbsUp, Scale, UserRound } from "lucide-react";
import { clock } from "../lib/format";
import { Face } from "../components/shared/Face";
import { recipes, prefersReducedMotion, reducedFallback } from "../motion";
import { LeaningMeter } from "./LeaningMeter";
import type { PersonImage, PersonSummary, PersonType, StanceLabel } from "./model";

const TYPE: Record<PersonType, [string, typeof Landmark]> = {
  official: ["Official", Landmark], staff: ["City staff", Briefcase], resident: ["Resident", House],
  lobbyist: ["Lobbyist", Briefcase], org_rep: ["Org rep", Building2], other: ["Speaker", Mic],
};

export const TypeChip = ({ type }: { type: PersonType }) => {
  const [label, Icon] = TYPE[type];
  return <span className="p-type"><Icon size={11} aria-hidden />{label}</span>;
};

export const AreaChip = ({ children }: { children: ReactNode }) => <span className="p-area">{children}</span>;

const STANCE = { supports: ["positive", "Supports", ThumbsUp], opposes: ["negative", "Opposes", ThumbsDown], mixed: ["warning", "Mixed", Scale] } as const;
export function StanceChip({ stance }: { stance: StanceLabel }) {
  const [tone, label, Icon] = STANCE[stance];
  return <span className={`p-stance p-stance--${tone}`}><Icon size={11} aria-hidden />{label}</span>;
}

/** Portrait (circle) or video frame (rounded rectangle + film icon). */
export function PersonImg({ image, name, size = 36, w = 48, h = 32, label = false }: { image: PersonImage; name: string; size?: number; w?: number; h?: number; label?: boolean }) {
  if (image.kind === "portrait") return <Face name={name} photoUrl={image.url} size={size} />;
  if (image.kind === "frame") {
    return (
      <span className="p-frame" style={{ width: w, height: h, backgroundImage: `url("${image.url}")` }} role="img" aria-label={`Video frame of ${name} at ${clock(image.at)}`}>
        <span className="p-frame__tag"><Film size={label ? 10 : 8} aria-hidden />{label && `Frame at ${clock(image.at)}`}</span>
      </span>
    );
  }
  return <span className="p-frame p-frame--none" style={{ width: w, height: h }} role="img" aria-label={name}><UserRound size={14} aria-hidden /></span>;
}

export const meetingHref = (meetingId: string, at?: number) => `/dashboard/meetings/${encodeURIComponent(meetingId)}${at != null ? `?t=${clock(at)}` : ""}`;

export const Watch = ({ meetingId, at, children }: { meetingId: string; at: number; children?: ReactNode }) => (
  <a className="p-watch" href={meetingHref(meetingId, at)}><Play size={10} aria-hidden />{children ?? clock(at)}</a>
);

export const profileHref = (id: string) => `/dashboard/people/${encodeURIComponent(id)}`;

export const mdy = (d: string, opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" }) =>
  d ? new Date(`${d.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", ...opts }) : "";

export const money = (n: number) => `$${Math.round(n).toLocaleString("en-US")}`;
export const moneyShort = (n: number) => (n >= 1e6 ? `$${(n / 1e6).toFixed(2).replace(/0$/, "")}M` : money(n));

/**
 * Profile peek (screenshots/24 card 1): hover or focus a name for 300 ms to see their leaning, latest position
 * and when they last spoke. Click opens the profile.
 */
export const PeekName = memo(function PeekName({ person, className, children }: { person: PersonSummary; className?: string; children?: ReactNode }) {
  const ref = useRef<HTMLAnchorElement>(null);
  const timer = useRef(0);
  const [open, setOpen] = useState<DOMRect | null>(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const show = () => { clearTimeout(timer.current); timer.current = window.setTimeout(() => setOpen(ref.current!.getBoundingClientRect()), recipes.peek.hoverIntentMs); };
  const hide = () => { clearTimeout(timer.current); setOpen(null); };
  return (
    <>
      <a ref={ref} className={className} href={profileHref(person.id)} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>{children ?? person.name}</a>
      {open && createPortal(<Peek person={person} rect={open} />, document.body)}
    </>
  );
});

function Peek({ person, rect }: { person: PersonSummary; rect: DOMRect }) {
  const el = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const r = recipes.peek;
    el.current?.animate(prefersReducedMotion() ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 0, transform: `scale(${r.from.scale})` }, { opacity: 1, transform: "none" }],
      { duration: prefersReducedMotion() ? reducedFallback.duration : r.duration, easing: r.easing });
  }, []);
  return (
    <div ref={el} className="p-peek" role="tooltip" style={{ left: rect.left + scrollX, top: rect.bottom + scrollY + 8 }}>
      <div className="p-peek__head">
        <PersonImg image={person.image} name={person.name} size={34} w={44} h={30} />
        <span><b>{person.name}</b><small>{[person.type === "official" ? "Alderperson" : TYPE[person.type][0], person.ward ? `Ward ${person.ward}` : null].filter(Boolean).join(" · ")}</small></span>
      </div>
      {person.leaning && <LeaningMeter estimate={person.leaning} size="mini" />}
      {person.peek && <div className="p-peek__pos"><span>{person.peek.label}</span><StanceChip stance={person.peek.stance} /></div>}
      {person.lastSpoke && <span className="p-watch"><Play size={10} aria-hidden />Last spoke {mdy(person.lastSpoke.date)} · {clock(person.lastSpoke.at)}</span>}
    </div>
  );
}
